import React, { useState } from "react";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { DocumentItemsFilter } from "src/pages/App/pages/Organization/pages/Document/components/DocumentItemsFilter";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { ReactComponent as DropdownSVG } from "go-core/images/svg/dropdown.svg";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { BundleApi, BundleItemApi } from "../../../../../../../../../../services/Api/Organization/types";

interface Props {
	bundle: BundleApi;
}

const BundlePreviewItemsCard = ({ bundle }: Props) => {
	const [filteredBundleItems, setFilteredBundleItems] = useState<BundleItemApi[]>(bundle.items);
	const { t } = useTranslation();
	const [shownIndexes, setShownIndexes] = useState<number[]>([]);
	const organization = useSelector(selectOrganization);

	const isRowIndexShown = (index: number) => {
		return shownIndexes.includes(index);
	};

	const handleShowMore = (index: number) => {
		if (isRowIndexShown(index)) {
			const indexes = shownIndexes;
			indexes.splice(shownIndexes.indexOf(index), 1);
			setShownIndexes([...indexes]);
		} else {
			setShownIndexes((indexes) => [...indexes, index]);
		}
	};

	return (
		<Card className="card-box positions-card">
			<Card.Body>
				<h5 className="card-title">{t("common.word.items")}</h5>
				<div className="table-responsive">
					<table className="table table-preview">
						<thead>
							<tr>
								<th className="action" />
								<th>{t("lib:common.word.name")}</th>
								<th>{t("common.word.quantity")}</th>
								<th>{t("modules.bundle.field.quantity_produced.title")}</th>
								<th>{t("common.word.item_cost")}</th>
								<th>{t("common.word.total_cost")}</th>
							</tr>
						</thead>
						<tbody>
							<DocumentItemsFilter
								numberOfColumns={7}
								items={bundle.items}
								numberOfFilteredItems={filteredBundleItems.length}
								setItems={setFilteredBundleItems}
							/>
							{filteredBundleItems.map((item, itemIndex) => {
								const indexInOriginalItemsArray = bundle.items.findIndex(
									(originalItem) => originalItem.id === item.id
								);

								return (
									<React.Fragment key={item.id}>
										<tr>
											<td className="action">
												{item?.items?.length > 0 && (
													<DropdownSVG
														className="icon"
														id={`icon-${itemIndex}`}
														style={{
															transform: isRowIndexShown(itemIndex)
																? "rotate(180deg)"
																: "unset",
														}}
														onClick={() => handleShowMore(itemIndex)}
													/>
												)}
											</td>
											<td className="w-40">
												<div className="d-flex flex-column">
													<div className="d-flex align-items-center column-gap-2">
														<span>{`${indexInOriginalItemsArray + 1}.`}</span>
														<Link
															rel="noreferrer"
															target="_blank"
															to={`/${organization.id}/products/${item.product?.id}/edit`}
														>
															{item.product?.name}
														</Link>
													</div>
													{item?.comment && (
														<small className="text-dark">{item.comment}</small>
													)}
												</div>
											</td>
											<td>{item.quantity?.toFixed(5)}</td>
											<td>{item.quantity_produced?.toFixed(5)}</td>
											<td>
												{item.unit_fifo_cost_net_money
													? FormatMoney(item.unit_fifo_cost_net_money)
													: "-"}
											</td>
											<td>
												{item.total_fifo_cost_net_money
													? FormatMoney(item.total_fifo_cost_net_money)
													: "-"}
											</td>
										</tr>
										{isRowIndexShown(itemIndex) &&
											item.items.map((subItem, subItemIndex) => (
												<tr key={`transfer-item-${itemIndex}-item-${subItemIndex}`}>
													<td className="action" />
													<td>
														<Link
															rel="noreferrer"
															target="_blank"
															to={`/${organization.id}/products/${subItem.product_id}/edit`}
															className="ms-4"
														>
															{subItem.product?.name}
														</Link>
													</td>
													<td>{subItem.quantity?.toFixed(5)}</td>
													<td />
													<td>
														{subItem?.unit_fifo_cost_net_money
															? FormatMoney(subItem.unit_fifo_cost_net_money)
															: "-"}
													</td>
													<td>
														{subItem?.total_fifo_cost_net_money
															? FormatMoney(subItem.total_fifo_cost_net_money)
															: "-"}
													</td>
												</tr>
											))}
									</React.Fragment>
								);
							})}
							<tr className="single-summary-row">
								<td colSpan={4} />
								<td>{t("common.word.total_cost")}</td>
								<td className="text-end">
									{bundle.total_fifo_cost_net_money
										? FormatMoney(bundle.total_fifo_cost_net_money)
										: "-"}
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</Card.Body>
		</Card>
	);
};

export default BundlePreviewItemsCard;
